'use client'

import clsx from 'clsx'
import { motion } from 'framer-motion'
import { LandingRating } from '../rating/landing-rating'
import { LandingAvatar, type SocialProofItem } from './landing-avatar'

const avatarItemsDefault = [
  {
    imageSrc: '/pageui/people/1.webp',
    name: 'John Doe',
  },
  {
    imageSrc: '/pageui/people/2.webp',
    name: 'Jane Doe',
  },
  {
    imageSrc: '/pageui/people/3.webp',
    name: 'Alice Doe',
  },
  {
    imageSrc: '/pageui/people/4.webp',
    name: 'Bob Doe',
  },
  {
    imageSrc: '/pageui/people/5.webp',
    name: 'Eve Doe',
  },
]

/**
 * Shows social proof with avatars, number of users and an optional rating.
 *
 * Use this to show proof of existing, happy customers & increase trust.
 */
export const LandingSocialProof = ({
  children,
  className,
  avatarItems = avatarItemsDefault,
  numberOfUsers = 100,
  suffixText = 'happy users',
  showRating,
  disableAnimation,
}: {
  children?: React.ReactNode
  className?: string
  avatarItems?: SocialProofItem[]
  numberOfUsers: number
  suffixText?: string
  showRating?: boolean
  disableAnimation?: boolean
}) => {
  const numberText =
    numberOfUsers > 1000 ? `${(numberOfUsers / 1000).toFixed(0)}k` : `${numberOfUsers}`

  const fadeUpVariants = {
    initial: {
      opacity: 0,
      y: 24,
    },
    animate: {
      opacity: 1,
      y: 0,
    },
  }

  return (
    <motion.div
      className={clsx('group flex flex-wrap gap-2', className)}
      initial="initial"
      animate="animate"
      variants={fadeUpVariants}
      transition={{
        duration: 0.6,
        delay: 0.1,
        ease: [0.21, 0.47, 0.32, 0.98],
        type: 'spring',
      }}
    >
      <div className={clsx('flex gap-1')}>
        {avatarItems.map((avatarItem, index) => (
          <LandingAvatar
            key={index}
            imageSrc={avatarItem.imageSrc}
            name={avatarItem.name}
            className={clsx(
              'relative',
              !disableAnimation ? 'md:group-hover:-ml-0.5 transition-all duration-300' : '',
              index === 1 || index === 2 ? '-ml-4' : '',
              index === 3 ? '-ml-5' : '',
              index > 3 ? '-ml-6' : '',
            )}
          />
        ))}

        <div
          className={clsx(
            !disableAnimation ? 'md:group-hover:-ml-0.5 transition-all duration-300' : '',
            '-ml-5 relative flex h-9 w-9 items-center justify-center rounded-full border-2 border-primary-100 border-solid bg-background bg-primary-100 text-primary text-xs',
          )}
        >
          {numberText}+
        </div>
      </div>

      <div className="flex flex-col justify-center gap-1">
        {showRating ? <LandingRating /> : null}

        {!children ? (
          <p className="max-w-sm text-sm">
            {/* {numberText}+  */}
            {suffixText}
          </p>
        ) : (
          children
        )}
      </div>
    </motion.div>
  )
}
